import { useEffect } from "react";
import { Stack } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import Button from "@mui/material/Button";
import LeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import RightIcon from "@mui/icons-material/KeyboardArrowRight";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PauseIcon from "@mui/icons-material/Pause";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import PollIcon from "@mui/icons-material/Poll";
import GlossaryIcon from "@mui/icons-material/AutoStoriesOutlined";
import { PLAYER_STATUS, BUTTON_THEME, PALETTE, DIRECTION } from "../constants";
import SlideProgress from "./SlideProgress";
import { TUTORIAL_TOOLTIP_CONFIGS } from "../hooks/useTutorialTooltips";
import TutorialTooltip from "./TutorialTooltip/TutorialTooltip";

const PLAYER_STATUS_ICON_DATA = {
  [PLAYER_STATUS.POLLING]: { IconComponent: PollIcon, color: "#69b8e3" },
  [PLAYER_STATUS.PAUSED]: { IconComponent: StopCircleIcon, color: "#FB923C" },
  [PLAYER_STATUS.PLAYING]: {
    IconComponent: AccountCircleIcon,
    color: "#22C55E",
  },
};

const { PREV, NEXT } = DIRECTION;

const Controls = ({
  disableArrowControls,
  updateSlideChangeInfo,
  ctrlBarData,
  numOfSlides,
  isFullscreen,
  isShowingGlossary,
  handleFullscreenToggle,
  handleGlossaryToggle,
  playerStatus,
  slideSections,
  slidesMarks,
  slideNumsToHideSlideDisplay,
  thumbnailImages,
  isGamePlaying,
  setIsGamePlaying,
  shouldHideSlideDisplay,
  handleNavigation,
  setTimerModalInfo,
  activeTutorialId,
  isTutorialTooltipBlockingInputs,
  completeActiveTutorial,
  isSessionLoaded,
}) => {
  // clickers emulates Page/Arrow key presses
  useEffect(() => {
    function keyDownListener(e) {
      if (
        disableArrowControls ||
        isTutorialTooltipBlockingInputs ||
        !isSessionLoaded
      ) {
        return;
      }
      if (isShowingGlossary) return;
      if (["PageUp", "ArrowUp", "ArrowLeft"].includes(e.key)) {
        handleNavigation(PREV);
      } else if (["PageDown", "ArrowDown", "ArrowRight"].includes(e.key)) {
        handleNavigation(NEXT);
      }
    }

    document.addEventListener("keydown", keyDownListener);

    return () => {
      document.removeEventListener("keydown", keyDownListener);
    };
  }, [
    handleNavigation,
    disableArrowControls,
    isShowingGlossary,
    isTutorialTooltipBlockingInputs,
    isSessionLoaded,
  ]);

  const handleNavigationWrapper = (dir) => {
    // arrow keys are disabled on slides with timers and navigation
    // with control left and right buttons are disabled too and should
    // open end early timer modal
    if (disableArrowControls) {
      setTimerModalInfo({ isVisible: true, endTimerEarlyDirection: dir });
    } else {
      handleNavigation(dir);
    }
  };

  return (
    <div className="slides-controls">
      {!!slideSections.length && (
        <Stack
          direction="column"
          justifyContent="flex-start"
          alignItems="center"
          height="100%"
        >
          <SlideProgress
            sections={slideSections}
            currSlideInfo={ctrlBarData.curr}
            numOfSlides={numOfSlides}
            slidesMarks={slidesMarks}
            updateSlideChangeInfo={updateSlideChangeInfo}
            slideNumsToHideSlideDisplay={slideNumsToHideSlideDisplay}
            thumbnailImages={thumbnailImages}
            isGamePlaying={isGamePlaying}
            shouldHideSlideDisplay={shouldHideSlideDisplay}
          />
          <div
            className="ctrl-bar-outer-container "
            /** @todo restructure DOM tree to eliminate need to do this? */
            /*
            need to move controls z-index up so that the tooltip overlay can be
            placed over the slide progress bar
            */
            style={!!activeTutorialId ? { zIndex: "2" } : {}}
          >
            <div
              className="ctrl-bar-inner-container "
              style={{
                backgroundColor: isGamePlaying
                  ? PALETTE.darkGreen
                  : PALETTE.black,
              }}
            >
              <div className="ctrl-bar-left">
                {isGamePlaying ? (
                  <PauseGameButton setIsGamePlaying={setIsGamePlaying} />
                ) : (
                  <Stack direction="row" spacing={1}>
                    <SlideNavButton
                      Icon={LeftIcon}
                      disabled={ctrlBarData.curr.slideNumber === 1}
                      onClick={() => handleNavigationWrapper(PREV)}
                    />
                    <div className="ctrl-bar-text slide-number">
                      <p>{ctrlBarData.curr.slideNumber}</p>
                    </div>
                    <SlideNavButton
                      Icon={RightIcon}
                      disabled={ctrlBarData.curr.slideNumber === numOfSlides}
                      onClick={() => handleNavigationWrapper(NEXT)}
                    />
                  </Stack>
                )}
              </div>
              <div className="ctrl-bar-right">
                <div className="ctrl-bar-player-status-outer-container">
                  <TutorialTooltip
                    config={TUTORIAL_TOOLTIP_CONFIGS.STUDENT_STATUS}
                    activeTutorialId={activeTutorialId}
                    completeTutorial={completeActiveTutorial}
                  >
                    <div className="ctrl-bar-player-status-inner-container">
                      <PlayerStatusIcon playerStatus={playerStatus} />
                      <div className="ctrl-bar-text">
                        <p>Students {playerStatus}</p>
                      </div>
                    </div>
                  </TutorialTooltip>
                </div>
                <div className="ctrl-bar-divider" />
                <div className="ctrl-bar-btn-container">
                  <TutorialTooltip
                    config={TUTORIAL_TOOLTIP_CONFIGS.GLOSSARY}
                    activeTutorialId={activeTutorialId}
                    completeTutorial={completeActiveTutorial}
                    onPrimaryBtnClick={handleGlossaryToggle}
                  >
                    <ToggleGlossaryButton
                      tabIndex={0}
                      onClick={handleGlossaryToggle}
                      onKeyDown={({ key }) => {
                        if (key === "Enter") {
                          handleGlossaryToggle();
                        }
                      }}
                    />
                  </TutorialTooltip>
                </div>
                <div className="ctrl-bar-btn-container">
                  <ToggleFullscreenButton
                    Icon={isFullscreen ? FullscreenExitIcon : FullscreenIcon}
                    tabIndex={0}
                    onClick={handleFullscreenToggle}
                    onKeyDown={({ key }) => {
                      if (key === "Enter") {
                        handleFullscreenToggle();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </Stack>
      )}
    </div>
  );
};

const ToggleGlossaryButton = (props) => {
  return (
    <div className="ctrl-bar-responsive-icon">
      <CtrlBarIconButton {...props} Icon={GlossaryIcon} />
    </div>
  );
};

/** @todo fix button icon to display correct icon when using hotkey F11 to toggle fullscreen? */
const ToggleFullscreenButton = (props) => {
  return (
    <div className="ctrl-bar-responsive-icon">
      <CtrlBarIconButton {...props} />
    </div>
  );
};

const PlayerStatusIcon = ({ playerStatus }) => {
  const { IconComponent, color } = PLAYER_STATUS_ICON_DATA[playerStatus];

  return (
    <div className="ctrl-bar-player-status-icon ctrl-bar-responsive-icon">
      <IconComponent
        fontSize="inherit"
        sx={{
          color,
        }}
      />
    </div>
  );
};

const SlideNavButton = (props) => (
  <div className="ctrl-bar-responsive-icon">
    <CtrlBarIconButton {...props} />
  </div>
);

const CtrlBarIconButton = ({ Icon, onClick, ...otherProps }) => (
  <Icon
    className="ctrl-bar-icon-btn"
    fontSize="inherit"
    onClick={(e) => {
      e.currentTarget.blur();
      onClick();
    }}
    {...otherProps}
  />
);

const PauseGameButton = ({ setIsGamePlaying }) => (
  <ThemeProvider theme={BUTTON_THEME}>
    <Button
      id="game-pause-btn"
      onClick={() => setIsGamePlaying(false)}
      variant="contained"
      startIcon={<PauseIcon />}
      color="primary"
    >
      Pause Teacher Play
    </Button>
  </ThemeProvider>
);
export default Controls;
